import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { 
  Plus, LogOut, Video, Clock, 
  CheckCircle2, AlertCircle, Instagram, 
  Linkedin, Trash2, Eye, Crown, Shield
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { API_URL, BASE_URL } from "../config/api";

const API = API_URL;

const Dashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: getAuthHeaders()
      });
      setProjects(response.data);
    } catch (error) {
      toast.error("Kon projecten niet laden");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const deleteProject = async (projectId) => {
    // Direct delete without browser confirm (which can be blocked)
    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success("Project verwijderd");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kon project niet verwijderen");
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case "instagram":
        return <Instagram className="w-4 h-4" />;
      case "tiktok":
        return <Video className="w-4 h-4" />;
      case "linkedin":
        return <Linkedin className="w-4 h-4" />;
      default:
        return <Video className="w-4 h-4" />;
    }
  };

  const getPlatformStyle = (platform) => {
    switch (platform) {
      case "instagram":
        return "bg-gradient-to-r from-purple-500 to-pink-500 text-white";
      case "tiktok":
        return "bg-black text-white";
      case "linkedin":
        return "bg-blue-600 text-white";
      default:
        return "bg-slate-600 text-white";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Voltooid
          </Badge>
        );
      case "generating":
        return (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
            <Clock className="w-3 h-3 mr-1 animate-spin" />
            Bezig
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
            <AlertCircle className="w-3 h-3 mr-1" />
            Mislukt
          </Badge>
        );
      default:
        return (
          <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">
            <Clock className="w-3 h-3 mr-1" />
            Concept
          </Badge>
        );
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Je bent uitgelogd");
  };

  return (
    <div className="min-h-screen bg-slate-950 dark-mode">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <Logo size="xl" linkTo="/dashboard" variant="light" />
          <div className="flex items-center gap-4">
            {user?.is_admin && (
              <Link to="/admin">
                <Button 
                  variant="ghost"
                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 flex items-center gap-2"
                  data-testid="admin-btn"
                >
                  <Shield className="w-4 h-4" />
                  <span className="hidden md:inline">Admin</span>
                </Button>
              </Link>
            )}
            <Link to="/subscription">
              <Button 
                variant="ghost"
                className="text-slate-400 hover:text-white hover:bg-slate-800 flex items-center gap-2"
                data-testid="subscription-btn"
              >
                <Crown className="w-4 h-4 text-amber-400" />
                <span className="hidden md:inline capitalize">{user?.subscription_plan || "demo"}</span>
              </Button>
            </Link>
            <span className="text-slate-400 text-sm hidden md:block">
              {user?.company_name}
            </span>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-32 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Mijn Projecten
              </h1>
              <p className="text-slate-400 mt-1">
                Beheer en maak je video-advertenties
              </p>
            </div>
            <Link to="/create" className="shrink-0">
              <Button 
                data-testid="create-project-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-5 rounded-xl shadow-lg shadow-indigo-600/25 transition-all duration-300 active:scale-95 whitespace-nowrap"
              >
                <Plus className="w-5 h-5 mr-2" />
                Nieuwe Video
              </Button>
            </Link>
          </div>

          {/* Projects Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : projects.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div className="w-20 h-20 rounded-2xl bg-slate-800 flex items-center justify-center mx-auto mb-6">
                <Video className="w-10 h-10 text-slate-600" />
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Nog geen projecten
              </h2>
              <p className="text-slate-400 mb-6 max-w-md mx-auto">
                Maak je eerste video-advertentie en bereik meer klanten op social media
              </p>
              <Link to="/create">
                <Button 
                  data-testid="empty-create-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-5 rounded-xl"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Maak Eerste Video
                </Button>
              </Link>
            </motion.div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="bg-slate-900/80 border-slate-800 hover:border-slate-700 transition-all duration-300 group overflow-hidden">
                    {/* Preview */}
                    <div className="aspect-video bg-slate-800 relative overflow-hidden">
                      {project.product_image_url ? (
                        <img 
                          src={`${BASE_URL}${project.product_image_url}`}
                          alt={project.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-12 h-12 text-slate-600" />
                        </div>
                      )}
                      {/* Platform Badge */}
                      <div className="absolute top-3 left-3">
                        <Badge className={`${getPlatformStyle(project.platform)} border-0 px-3 py-1`}>
                          {getPlatformIcon(project.platform)}
                          <span className="ml-1 capitalize">{project.platform}</span>
                        </Badge>
                      </div>
                      {/* Status Badge */}
                      <div className="absolute top-3 right-3">
                        {getStatusBadge(project.status)}
                      </div>
                    </div>
                    
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-lg text-white mb-1 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {project.name}
                      </h3>
                      <p className="text-slate-400 text-sm line-clamp-2 mb-4">
                        {project.ad_text || project.description || "Geen beschrijving"}
                      </p>
                      
                      <div className="flex items-center gap-2">
                        <Link to={`/project/${project.id}`} className="flex-1">
                          <Button 
                            data-testid={`view-project-${project.id}`}
                            variant="outline" 
                            className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Bekijken
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-project-${project.id}`}
                          onClick={() => deleteProject(project.id)}
                          className="text-slate-500 hover:text-red-400 hover:bg-red-400/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
